import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

import { useGetLivestreamingM3uFilesQuery } from '../../redux/apis/analyticsManagement/aMLiveStreamingStatsApiSlice';
import ComponentLoader from '../other/ComponentLoader';
import { Card, CardBody, CardExpandToggler } from '../other/card/card';
import getLivestreamingM3uInsightOptions from './charts/livestreamingM3uInsightOptions';

const M3U_INSIGHT_CHART_HEIGHT = 380;

const formatMonthCategory = monthStr => {
  const s = String(monthStr ?? '');
  const m = /^(\d{4})-(\d{2})/.exec(s);
  if (!m) return s || '—';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const LiveStreamingM3uFilesPerMonth = () => {
  const { data, isLoading, isError, error } = useGetLivestreamingM3uFilesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [chartThemeKey, setChartThemeKey] = useState(0);

  useEffect(() => {
    const onThemeReload = () => setChartThemeKey(k => k + 1);
    document.addEventListener('theme-reload', onThemeReload);
    return () => document.removeEventListener('theme-reload', onThemeReload);
  }, []);

  const sorted = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];
    rows.sort((a, b) => String(a?.month ?? '').localeCompare(String(b?.month ?? '')));
    return rows;
  }, [data]);

  const categories = useMemo(() => sorted.map(r => formatMonthCategory(r?.month)), [sorted]);
  const series = useMemo(
    () => [{ name: 'Detected M3U files', data: sorted.map(r => Number(r?.count) || 0) }],
    [sorted]
  );

  const chartOptions = useMemo(() => getLivestreamingM3uInsightOptions(categories), [categories]);
  const hasData = sorted.length > 0;

  return (
    <div className='row g-2 mt-1'>
      <div className='col-12'>
        <Card className='mb-0'>
          <CardBody className='relative p-0'>
            {isError ? (
              <div className='absolute inset-0 d-flex items-center justify-center p-3 text-danger small bg-white bg-opacity-75'>
                Failed to load.
                {error?.data?.message ? ` ${error.data.message}` : ''}
              </div>
            ) : null}

            <div className='p-4 d-flex flex-wrap justify-content-between gap-2'>
              <div className='d-flex flex-column gap-1'>
                <div className='d-flex fw-bold small'>M3U/playlist Files insight</div>
              </div>
              <div className='align-self-start'>
                <CardExpandToggler />
              </div>
            </div>

            <div className='p-4 pt-0'>
              <div className='small text-body-secondary'>
                <div className='mt-2 position-relative' style={{ minHeight: M3U_INSIGHT_CHART_HEIGHT }}>
                  {isLoading ? <ComponentLoader /> : null}
                  {hasData ? (
                    <Chart
                      key={`ls-m3u-insight-${chartThemeKey}`}
                      type='line'
                      height={M3U_INSIGHT_CHART_HEIGHT}
                      options={chartOptions}
                      series={series}
                    />
                  ) : !isLoading ? (
                    <div
                      className='d-flex align-items-center justify-content-center text-center px-2'
                      style={{ minHeight: M3U_INSIGHT_CHART_HEIGHT }}
                    >
                      No M3U/playlist file data for the current year.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default LiveStreamingM3uFilesPerMonth;
